import React from 'react';

import {recordRumAction} from '../../util/datadog/datadogRum';
import {PanelPopupMode} from '../Panel/types';
import {DEFAULT_LINE_PLOT_MAX_GROUP_RUNS} from '../WorkspaceDrawer/Settings/runLinePlots/maxRunDefaults';
import {getTitleFromConfig} from './common';
import {GraphWrapper} from './Graph';
import {type GraphAdapterProps} from './GraphAdapterProps';
import {getGraphAnnotations} from './graphAnnotations';
import {getGraphInteraction} from './graphInteraction';
import {
  DEFAULT_GRAPH_LEGEND_POSITION,
  type GraphAxis,
  type GraphRenderProps,
} from './graphRenderProps';
import {getGraphSelection} from './graphSelection';
import {getGraphTooltip, getLegendRunLink} from './graphTooltip';
import {usePreparedChartSeries} from './usePreparedChartSeries';
import {usePreparedGraphZoom} from './zoom/usePreparedGraphZoom';

/** Compatibility host prepares numeric snapshots and purpose-based renderer inputs. */
export type GraphProps = GraphAdapterProps;

export function GraphAdapter(props: GraphProps) {
  const {series, seriesStylesById, seriesMetadataById} = usePreparedChartSeries(
    props.lines
  );
  const controlledZoom = usePreparedGraphZoom(props, series);
  function resetZoom() {
    controlledZoom.onZoom({intent: {shouldZoomX: false, shouldZoomY: false}});
  }
  const zoom: GraphRenderProps['zoom'] = {
    isActive: controlledZoom.activeZoom,
    isZooming: controlledZoom.zooming,
    isZoomedXMax: controlledZoom.isZoomedXMax,
    isZoomedXMin: controlledZoom.isZoomedXMin,
    onReset: resetZoom,
    onZoom: controlledZoom.onZoom,
    xDomain: controlledZoom.xDomain,
    yDomain: controlledZoom.yDomain,
  };
  const interaction = getGraphInteraction(props.highlight);
  function openPanelEditor() {
    props.panelTitle.setPanelPopupMode(PanelPopupMode.EDIT);
  }
  const titleText = getTitleFromConfig(props.config);
  const panel: GraphRenderProps['panel'] = {
    id: props.panelId,
    isMultiMetric: props.plotSettings.isMultiMetric ?? false,
  };
  const title: GraphRenderProps['title'] = {
    onActivate: openPanelEditor,
    renderHighlightedText: props.panelTitle.isFullScreen
      ? undefined
      : props.panelTitle.makeHighlightSearchTitle,
    text: titleText,
  };
  const layout: GraphRenderProps['layout'] = {sizeRef: props.sizeRef};
  const annotationItems = props.issueLines?.map(({id, status, xValue}) => ({
    id,
    status,
    xValue,
  }));
  const annotations = getGraphAnnotations(annotationItems, props.issueMarkers);
  const selection = getGraphSelection(props.config);
  const errors: GraphRenderProps['errors'] = {
    hasExpressions: selection.hasExpressions,
    hasUnexpectedError: props.hasError,
    metricKeys: selection.metrics,
    savedXKey: props.config.xAxis,
  };
  const loading: GraphRenderProps['loading'] = {
    isActive: props.loading,
    isReady: props.data.histories != null,
    regexSettlingKey: selection.regexDataKey,
  };
  const axis: GraphAxis = {
    outlierPolicy: props.display.excludeOutliers,
    xKey: props.plotSettings.xAxis,
    xIsDatetime: props.plotSettings.xAxisIsDatetime,
    xTitle: props.config.xAxisTitle,
    xScale: props.plotSettings.xLogScale ? 'log' : 'linear',
    yTitle: props.config.yAxisTitle,
    yScale: props.plotSettings.yLogScale ? 'log' : 'linear',
  };
  const fallbacks: GraphRenderProps['fallbacks'] = {
    barYMin: props.config.yAxisMin,
    barYMax: props.config.yAxisMax,
    singlePointStyle: props.display.singlePointRenderMode,
  };
  const appearance: GraphRenderProps['appearance'] = {
    fontSize: props.config.fontSize ?? 'auto',
    isDarkMode: props.plotSettings.isDarkMode,
  };
  const rendering: GraphRenderProps['rendering'] = {
    allowUPlot: props.plotSettings.uplotEnabled,
    exportMode: props.exportMode ?? false,
    forceReactVis: props.plotSettings.forceReactVis === true,
    isFullFidelity: props.plotSettings.isFullFidelity,
    pointStrategy: props.plotSettings.pointVisualizationMethod,
    plotType: props.plotSettings.plotType,
  };
  const tooltip = getGraphTooltip(
    {
      colorRunNames: props.plotSettings.colorRunNames,
      colorPaletteOption: props.plotSettings.colorPaletteOption,
      displayFullRunName: props.plotSettings.displayFullRunName,
      entityName: props.plotSettings.entityName,
      getNamedWorkspaceSearch: props.plotSettings.getNamedWorkspaceSearch,
      hasMediaPanel: props.plotSettings.hasMediaPanel,
      highlightedCompanionRunOnly:
        props.plotSettings.highlightedCompanionRunOnly,
      isSingleRun: props.plotSettings.isSingleRun,
      onSnapMediaPanelsToStep: props.plotSettings.onSnapMediaPanelsToStep,
      projectName: props.plotSettings.projectName,
      showXRange: props.plotSettings.showXRange,
      singleRunNavParams: props.plotSettings.singleRunNavParams,
      tooltipNumberOfRuns: props.plotSettings.tooltipNumberOfRuns,
      xAxis: props.plotSettings.xAxis,
      xAxisFormat: props.plotSettings.xAxisFormat,
    },
    props.runNameTruncationType
  );
  const legend: GraphRenderProps['legend'] = {
    isSingleRun: props.plotSettings.isSingleRun,
    resolveRunLink: props.disableRunLinks ? undefined : getLegendRunLink,
    position: props.config.legendPosition ?? DEFAULT_GRAPH_LEGEND_POSITION,
    show: props.display.showLegend,
    colorPalette: props.plotSettings.colorPaletteOption,
    colorRunNames: props.plotSettings.colorRunNames,
  };
  const panelId = props.panelId;
  const setAllowUncappedMetrics = props.cappedPanel.setAllowUncappedMetrics;
  function onDelayedShown() {
    recordRumAction('system_metrics_capped_panel_mounted', {
      title: titleText,
      panelId,
    });
  }
  function onLoadAnyway() {
    recordRumAction('system_metrics_capped_panel_displayed', {
      title: titleText,
      panelId,
    });
    setAllowUncappedMetrics(true);
  }
  const fetchedCounts = Object.values(
    props.data.fetchedRunCountByRunsetId ?? {}
  );
  const notices: GraphRenderProps['notices'] = {
    displayRowLimitPerRunset: props.plotSettings.limit,
    fetchedRunCount: props.data.fetchedRunCount ?? 0,
    groupedIncludedRunCount: props.data.totalGroupedIncludedRuns ?? 0,
    groupInputRunLimitPerRunset:
      props.config.groupRunsLimit ?? DEFAULT_LINE_PLOT_MAX_GROUP_RUNS,
    groupingDepth: props.plotSettings.groupingDepth,
    hasGroupedRunsets: props.plotSettings.isAnyRunsetGrouped,
    hasSubsampledGroups: props.data.hasSubsampledGroups ?? false,
    isDelayed: props.cappedPanel.isDelayed ? true : undefined,
    isSamplingFallback: props.samplingWarning?.isSamplingFallback,
    maxFetchedRunsPerRunset:
      fetchedCounts.length === 0 ? 0 : Math.max(...fetchedCounts),
    metricCount: props.config.metrics?.length ?? 0,
    onDelayedNoticeShown: props.cappedPanel.isDelayed
      ? onDelayedShown
      : undefined,
    onLoadAnyway: props.cappedPanel.isDelayed ? onLoadAnyway : undefined,
    runsetCount: fetchedCounts.length,
    samplingWarnings: props.samplingWarning?.warningMessages,
    showLimitHelp: !props.readOnly,
  };
  return (
    <GraphWrapper
      axis={axis}
      fallbacks={fallbacks}
      appearance={appearance}
      rendering={rendering}
      errors={errors}
      layout={layout}
      loading={loading}
      panel={panel}
      title={title}
      notices={notices}
      legend={legend}
      interaction={interaction}
      annotations={annotations}
      series={series}
      seriesStylesById={seriesStylesById}
      seriesMetadataById={seriesMetadataById}
      tooltip={tooltip}
      zoom={zoom}
    />
  );
}
